import { useCallback, useEffect, useRef, useState } from 'react';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import qs, { IParseOptions, IStringifyOptions } from 'qs';
import useDebounce from './useDebounce';

// keep all filter settings keys in one place for usage visibility
enum QueryKeys {
  Explorer = 'workItems',
}

type KeyType = `${QueryKeys}` | (string & {});

const qsStringifyOptions: IStringifyOptions = {
  skipNulls: true,
  encode: false,
};

const qsParseOptions: IParseOptions = {
  ignoreQueryPrefix: true,
};

type QueryValue<T = any> = T;
type QueryRecord<T extends QueryValue = QueryValue> = Record<string, T>;

type Deserializer<T> = (value: string) => T;
type Serializer<T> = (value: T | undefined) => string;

const serializeQueryString = <T>(value: T) => {
  try {
    return qs.stringify(value, qsStringifyOptions);
  } catch (_error) {
    throw new Error('failed to serialize query entry');
  }
};

const deserializeQueryString = <T>(value: string): T => {
  try {
    return qs.parse(value, qsParseOptions) as T;
  } catch {
    return {} as T;
  }
};

const deserialize = <T>(value: string): T => deserializeQueryString(value);
const serialize = <T>(value: T | undefined): string => (value ? serializeQueryString(value) : '');

/**
 * When search is reset sync the internal state.
 * Might need an advanced solution for state sync if url ir partially reset.
 */
const useIsSearchEmpty = () => {
  const { search } = useLocation();
  return search.length === 0;
};

interface QuerySearchProps<T> extends NavigateOptions {
  serializer?: Serializer<QueryRecord<T>>;
  deserializer?: Deserializer<QueryRecord<T>>;
  delay?: number;
}

export const useQuerySearch = <T = QueryValue>(
  key: KeyType,
  {
    serializer = serialize,
    deserializer = deserialize,
    delay = 500,
    ...navigateOptions
  }: QuerySearchProps<T> = {},
) => {
  const loaded = useRef<boolean>(false);
  const navigate = useNavigate();
  const syncState = useIsSearchEmpty();

  const getDeserializedData = useCallback(
    (search: string, data?: T) => ({
      ...deserializer(search),
      ...(data !== undefined && { [key]: data }),
    }),
    [],
  );

  const [searchPath, setSearchPath] = useState<T>(
    () => getDeserializedData(window.location.search)[key],
  );

  const getReactiveDeserializedData = useCallback<() => QueryRecord>(
    () => () => getDeserializedData(window.location.search, searchPath),
    [getDeserializedData, searchPath],
  );

  // don't update url immediately and batch possible multiple updates in one page re-render (location changed)
  const getReactiveDeserializedDataDebounced = useDebounce(getReactiveDeserializedData, delay);
  useEffect(() => {
    if (loaded.current) {
      void navigate(
        {
          search: serializer(getReactiveDeserializedDataDebounced()),
        },
        navigateOptions,
      );
    } else {
      loaded.current = true;
    }
  }, [key, getReactiveDeserializedDataDebounced]);

  useEffect(() => {
    if (loaded.current && syncState) {
      setSearchPath(getDeserializedData(window.location.search)[key]);
    }
  }, [key, syncState, getDeserializedData]);

  return [searchPath, setSearchPath] as const;
};
